/**
 * 节点画布：拓扑序、上游汇总（供执行器使用）
 */

import { ecomDetailOutputUrls, exportEcomDetailNodeText } from '@/utils/ecomDetailNode'
import { exportScriptWriteNodeText } from '@/utils/scriptWriteNode'
import { exportUiDesignNodeText, uiDesignOutputUrls } from '@/utils/uiDesignNode'
import {
  buildImageAspectKeyFromStoryboardRatio,
  resolveStoryboardAspectRatio
} from '@/utils/storyboardNodeOptions'
import {
  cloneWorkflowStylePlain,
  isWorkflowStyleSet,
  workflowStyleFromStoryboardNode
} from '@/utils/workflowStyleApi'

export function topoSort(nodes, edges) {
  const ids = (nodes || []).map(n => n.id).filter(Boolean)
  const idSet = new Set(ids)
  const inD = Object.fromEntries(ids.map(id => [id, 0]))
  const adj = Object.fromEntries(ids.map(id => [id, []]))
  for (const e of edges || []) {
    if (!e || !e.from || !e.to) continue
    const a = e.from.nodeId
    const b = e.to.nodeId
    if (!idSet.has(a) || !idSet.has(b)) continue
    adj[a].push(b)
    inD[b] += 1
  }
  const q = ids.filter(id => inD[id] === 0)
  const out = []
  while (q.length) {
    const u = q.shift()
    out.push(u)
    for (const v of adj[u]) {
      inD[v] -= 1
      if (inD[v] === 0) q.push(v)
    }
  }
  if (out.length !== ids.length) {
    const err = new Error('画布存在循环连接，无法按序执行')
    err.code = 'CYCLE'
    throw err
  }
  return out
}

export function predecessorIds(nodeId, edges) {
  return (edges || []).filter(e => e && e.to && e.to.nodeId === nodeId).map(e => e.from.nodeId)
}

/**
 * 沿入边向上游 BFS，取最近的分镜脚本节点 workflowStyle
 * @returns {object|null}
 */
export function gatherUpstreamStoryboardWorkflowStyle(nodeId, nodesById, edges) {
  const visited = new Set()
  const queue = [...predecessorIds(nodeId, edges)]
  while (queue.length) {
    const pid = queue.shift()
    if (visited.has(pid)) continue
    visited.add(pid)
    const p = nodesById[pid]
    if (!p) continue
    if (p.type === 'storyboard' || p.type === 'storyPanel') {
      const ws = workflowStyleFromStoryboardNode(p)
      if (ws) return ws
    }
    for (const pp of predecessorIds(pid, edges)) {
      if (!visited.has(pp)) queue.push(pp)
    }
  }
  return null
}

/**
 * 图片节点出图用风格：本节点已选优先，否则继承上游分镜脚本风格
 * @returns {object|null}
 */
export function effectiveWorkflowStyleForImageNode(imageNode, nodesById, edges) {
  if (!imageNode || imageNode.type !== 'image' || !imageNode.data) return null
  const own = imageNode.data.workflowStyle
  if (isWorkflowStyleSet(own)) return cloneWorkflowStylePlain(own)
  return gatherUpstreamStoryboardWorkflowStyle(imageNode.id, nodesById, edges)
}

/**
 * 沿入边向上游 BFS，取最近分镜节点的画幅比例（16:9 | 9:16）
 * @returns {string|null}
 */
export function gatherUpstreamStoryboardAspectRatio(nodeId, nodesById, edges) {
  const visited = new Set()
  const queue = [...predecessorIds(nodeId, edges)]
  while (queue.length) {
    const pid = queue.shift()
    if (visited.has(pid)) continue
    visited.add(pid)
    const p = nodesById[pid]
    if (!p) continue
    if ((p.type === 'storyboard' || p.type === 'storyPanel') && p.data) {
      return resolveStoryboardAspectRatio(p.data)
    }
    for (const pp of predecessorIds(pid, edges)) {
      if (!visited.has(pp)) queue.push(pp)
    }
  }
  return null
}

function parseImageAspectKeyParts(aspectKey) {
  const k = String(aspectKey || '').trim()
  const m = k.match(/^(.+)_([124]k)$/i)
  if (m) return { ratioSlug: m[1], tier: m[2].toLowerCase() }
  return { ratioSlug: '16x9', tier: '2k' }
}

/**
 * 图片节点出图用 aspectKey：本节点手动改过比例则保留；否则继承上游分镜（保留本节点清晰度档位）
 * @returns {string}
 */
export function effectiveImageAspectKeyForImageNode(imageNode, nodesById, edges) {
  if (!imageNode || imageNode.type !== 'image' || !imageNode.data) return '16x9_2k'
  const ownKey = String(imageNode.data.aspectKey || '').trim() || '16x9_2k'
  if (imageNode.data.aspectRatioUserPick) return ownKey
  const sbRatio = gatherUpstreamStoryboardAspectRatio(imageNode.id, nodesById, edges)
  if (!sbRatio) return ownKey
  const { tier } = parseImageAspectKeyParts(ownKey)
  return buildImageAspectKeyFromStoryboardRatio(sbRatio, tier)
}

/**
 * 视频节点 ratio：本节点手动改过则保留；否则继承上游分镜
 * @returns {string}
 */
export function effectiveVideoRatioForVideoNode(videoNode, nodesById, edges) {
  if (!videoNode || videoNode.type !== 'video' || !videoNode.data) return '16:9'
  if (videoNode.data.ratioUserPick) {
    const r = videoNode.data.ratio != null ? String(videoNode.data.ratio).trim() : ''
    return r || '16:9'
  }
  const sbRatio = gatherUpstreamStoryboardAspectRatio(videoNode.id, nodesById, edges)
  if (sbRatio) return sbRatio
  const r = videoNode.data.ratio != null ? String(videoNode.data.ratio).trim() : ''
  return r || '16:9'
}

export function exportNodeText(n) {
  if (!n) return ''
  const d = n.data || {}
  if (n.type === 'note') return String(d.body || '').trim()
  if (n.type === 'storyboard') {
    const refined = String(d.output || '').trim()
    const draft = String(d.body || '').trim()
    const main = refined || draft
    if (!main) return ''
    return `【分镜脚本】\n${main}`
  }
  if (n.type === 'storyPanel') {
    const refined = String(d.output || '').trim()
    const draft = String(d.body || '').trim()
    const main = refined || draft
    if (!main) return ''
    return `【故事板】\n${main}`
  }
  if (n.type === 'scriptWrite') return exportScriptWriteNodeText(n)
  if (n.type === 'videoCopy') {
    const text = String(d.output || '').trim()
    if (!text) return ''
    const title = String(d.videoTitle || '').trim()
    return title ? `【短视频文案 · ${title}】\n${text}` : `【短视频文案】\n${text}`
  }
  if (n.type === 'llm') {
    const base = String(d.output || d.prompt || '').trim()
    const v = d.inlineVideoUrl ? String(d.inlineVideoUrl).trim() : ''
    if (v) return [base, `[视频] ${v}`].filter(Boolean).join('\n')
    return base
  }
  if (n.type === 'image') return `[图像] ${String(d.prompt || '').trim()}`.trim()
  if (n.type === 'ecomDetail') return exportEcomDetailNodeText(n)
  if (n.type === 'uiDesign') return exportUiDesignNodeText(n)
  if (n.type === 'video') return `[视频] ${String(d.prompt || '').trim()}`.trim()
  if (n.type === 'audio') {
    const t = String(d.synthText != null ? d.synthText : d.prompt || '').trim()
    return t ? `[音频] ${t}` : ''
  }
  return ''
}

export function gatherUpstreamText(nodeId, nodesById, edges) {
  const parts = []
  for (const pid of predecessorIds(nodeId, edges)) {
    const p = nodesById[pid]
    const t = exportNodeText(p)
    if (t) parts.push(t)
  }
  return parts.join('\n\n')
}

/**
 * 文生图 / 角色设计等：汇总上游文本时排除「分镜脚本」节点全文，避免整表分镜灌入提示词。
 * 仍保留上游 LLM、注释、图片/视频节点的文字字段等。
 */
export function gatherUpstreamTextForImagePrompt(nodeId, nodesById, edges) {
  const parts = []
  for (const pid of predecessorIds(nodeId, edges)) {
    const p = nodesById[pid]
    if (!p || p.type === 'storyboard' || p.type === 'storyPanel' || p.type === 'scriptWrite') continue
    const t = exportNodeText(p)
    if (t) parts.push(t)
  }
  return parts.join('\n\n')
}

/** 电商详情图：汇总上游图片节点的 prompt / URL（视觉 API 不可用时的文本推断素材） */
export function gatherUpstreamImageMetaForEcom(nodeId, nodesById, edges) {
  const lines = []
  let idx = 0
  for (const pid of predecessorIds(nodeId, edges)) {
    const p = nodesById[pid]
    if (!p) continue
    const d = p.data || {}
    if (p.type === 'image') {
      const u = String(d.imageUrl || '').trim()
      const pr = String(d.prompt || '').trim()
      if (!u && !pr) continue
      idx += 1
      const bits = [`参考图 ${idx}`]
      if (pr) bits.push(`节点描述：${pr}`)
      if (u) bits.push(`URL：${u}`)
      lines.push(bits.join('；'))
    } else if (p.type === 'ecomDetail') {
      const urls = ecomDetailOutputUrls(p)
      const pr = exportEcomDetailNodeText(p)
      if (urls.length || pr) {
        idx += 1
        const bits = [`上游电商详情图 ${idx}`]
        if (pr) bits.push(pr.slice(0, 800))
        if (urls.length) bits.push(`已出图 ${urls.length} 张`)
        lines.push(bits.join('；'))
      }
    } else if (p.type === 'directorDeck') {
      const u = directorDeckUpstreamImageUrl(p, nodesById, edges)
      if (u) {
        idx += 1
        lines.push(`参考图 ${idx}（3D 导演台导出）；URL：${u}`)
      }
    }
  }
  return lines.join('\n')
}

/**
 * 供语音合成使用：不包含 [视频]/[图像]/[音频] 等导出标记与内联 URL，避免被 TTS 读出来
 */
export function exportNodeTextForSpeech(n) {
  if (!n) return ''
  const d = n.data || {}
  if (n.type === 'note') return String(d.body || '').trim()
  if (n.type === 'storyboard' || n.type === 'storyPanel') {
    const refined = String(d.output || '').trim()
    if (refined) return refined
    return String(d.body || '').trim()
  }
  if (n.type === 'scriptWrite') {
    return String(d.output || d.body || '').trim()
  }
  if (n.type === 'videoCopy') {
    return String(d.output || '').trim()
  }
  if (n.type === 'llm') {
    return String(d.output || d.prompt || '').trim()
  }
  if (n.type === 'image') {
    return String(d.prompt || '').trim()
  }
  if (n.type === 'video') {
    return String(d.prompt || '').trim()
  }
  if (n.type === 'audio') {
    return String(d.synthText != null ? d.synthText : d.prompt || '').trim()
  }
  return ''
}

export function gatherUpstreamSpeechText(nodeId, nodesById, edges) {
  const parts = []
  for (const pid of predecessorIds(nodeId, edges)) {
    const p = nodesById[pid]
    const t = exportNodeTextForSpeech(p)
    if (t) parts.push(t)
  }
  return parts.join('\n\n')
}

/** 从导演台节点沿出边 BFS，找到最近的下游图片节点 id（与画布 Vue 侧逻辑一致） */
export function firstDownstreamImageNodeIdFromDeck(deckNodeId, nodesById, edges) {
  const start = String(deckNodeId || '').trim()
  if (!start || !nodesById || !nodesById[start]) return ''
  const visited = new Set([start])
  const queue = [start]
  while (queue.length) {
    const cur = queue.shift()
    const node = nodesById[cur]
    if (node && node.type === 'image') return cur
    for (const e of edges || []) {
      const from = e && e.from && e.from.nodeId
      if (from !== cur) continue
      const to = e && e.to && e.to.nodeId
      if (!to || visited.has(to)) continue
      if (!nodesById[to]) continue
      visited.add(to)
      queue.push(to)
    }
  }
  return ''
}

/**
 * 导演台作为上游时提供的「单张」图片 URL：优先本节点保存的导出预览，否则取 BFS 最近下游图片节点的出图。
 */
export function directorDeckUpstreamImageUrl(deckNode, nodesById, edges) {
  if (!deckNode || deckNode.type !== 'directorDeck') return ''
  const d = deckNode.data || {}
  const shot = String(d.directorShotPreviewUrl || '').trim()
  if (shot) return shot
  const imgId = firstDownstreamImageNodeIdFromDeck(deckNode.id, nodesById, edges)
  if (!imgId) return ''
  const img = nodesById[imgId]
  if (!img || img.type !== 'image') return ''
  const u = img.data && String(img.data.imageUrl || '').trim()
  return u || ''
}

export function gatherUpstreamImageUrls(nodeId, nodesById, edges) {
  const urls = []
  for (const pid of predecessorIds(nodeId, edges)) {
    const p = nodesById[pid]
    if (!p) continue
    const d = p.data || {}
    if (p.type === 'image' && d.imageUrl) {
      const u = String(d.imageUrl).trim()
      if (u) urls.push(u)
    } else if (p.type === 'ecomDetail') {
      for (const u of ecomDetailOutputUrls(p)) urls.push(u)
    } else if (p.type === 'uiDesign') {
      for (const u of uiDesignOutputUrls(p, nodesById)) urls.push(u)
    } else if (p.type === 'directorDeck') {
      const u = directorDeckUpstreamImageUrl(p, nodesById, edges)
      if (u) urls.push(u)
    }
  }
  return urls
}

/** 上游音频节点：参考 URL 或已合成音频 URL（用于音色克隆链） */
export function gatherUpstreamAudioUrls(nodeId, nodesById, edges) {
  const urls = []
  for (const pid of predecessorIds(nodeId, edges)) {
    const p = nodesById[pid]
    if (!p || p.type !== 'audio') continue
    const d = p.data || {}
    const u = String(d.referenceAudioUrl || d.audioUrl || '').trim()
    if (u) urls.push(u)
  }
  return urls
}

/** 上游视频节点已产出 / 已上传的视频 URL（用于 HappyHorse video-edit / 动作模仿等） */
export function gatherUpstreamVideoUrls(nodeId, nodesById, edges) {
  const urls = []
  for (const pid of predecessorIds(nodeId, edges)) {
    const p = nodesById[pid]
    if (!p) continue
    if (p.type !== 'video' && p.type !== 'animateMove') continue
    const d = p.data || {}
    const u = String(
      d.videoUrl || d.inlineVideoUrl || d.referenceVideoUrl || ''
    ).trim()
    if (u) urls.push(u)
  }
  return urls
}

export function pickFirstImageThumb(nodes) {
  for (const n of nodes || []) {
    if (n && n.type === 'image' && n.data && n.data.imageUrl) {
      const u = String(n.data.imageUrl).trim()
      if (u) return u
    }
    if (n && n.type === 'ecomDetail') {
      const urls = ecomDetailOutputUrls(n)
      if (urls.length) return urls[0]
    }
    if (n && n.type === 'uiDesign') {
      const urls = uiDesignOutputUrls(n, Object.fromEntries((nodes || []).map(x => [x.id, x])))
      if (urls.length) return urls[0]
    }
  }
  return ''
}

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms))
}

export { sleep }
